import React, { Component } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import omit from 'lodash/omit';

import { SuiContainer, SuiLabel, SuiButton, SuiPopover, SuiTextInput } from '@splashthat/sui';

import CloseIcon from '../../../icons/Close';
import AddFill from '../../../icons/AddFill';
import RemoveFill from '../../../icons/RemoveFill';

const BORDER_PICKER_POPOVER_TITLE = 'Advanced Styles';
const BORDER_PICKER_POPOVER_BODY = 'Border Size';
const BORDER_PICKER_POPOVER_FOOTER = 'Border Radius';

const RADIUS = 'RADIUS';
const TOP = 'TOP';
const BOTTOM = 'BOTTOM';
const RIGHT = 'RIGHT';
const LEFT = 'LEFT';

const BASE_CLASS_NAME = 'BorderPickerOptionsPopover';

export default class BorderPickerOptionsPopover extends Component {
    static propTypes = {
        showPopover: PropTypes.bool.isRequired,
        onClickClose: PropTypes.func.isRequired,
        anchorEl: PropTypes.object,
        minWidth: PropTypes.number.isRequired,
        borderRadius: PropTypes.number.isRequired,
        onBorderRadiusChanged: PropTypes.func.isRequired,
        borderWidths: PropTypes.object.isRequired,
        onChangeTopBorderWidth: PropTypes.func.isRequired,
        onChangeBottomBorderWidth: PropTypes.func.isRequired,
        onChangeRightBorderWidth: PropTypes.func.isRequired,
        onChangeLeftBorderWidth: PropTypes.func.isRequired,
        qaId: PropTypes.string.isRequired,
    };

    static defaultProps = {
        anchorEl: null,
    };

    state = {
        [RADIUS]: this.props.borderRadius,
        [TOP]: get(this.props.borderWidths, 'top'),
        [BOTTOM]: get(this.props.borderWidths, 'bottom'),
        [RIGHT]: get(this.props.borderWidths, 'right'),
        [LEFT]: get(this.props.borderWidths, 'left'),
    };

    wrapperRef = React.createRef();

    inputToChangeProp = {
        [RADIUS]: this.props.onBorderRadiusChanged,
        [TOP]: this.props.onChangeTopBorderWidth,
        [BOTTOM]: this.props.onChangeBottomBorderWidth,
        [RIGHT]: this.props.onChangeRightBorderWidth,
        [LEFT]: this.props.onChangeLeftBorderWidth,
    };

    componentDidMount = () => {
        document.addEventListener('click', this.handleOutsideClick, false);
    };

    componentWillUnmount = () => {
        document.removeEventListener('click', this.handleOutsideClick, false);
    };

    setInputFocused = (event) => {
        event.target.select();
    };

    handleOutsideClick = (event) => {
        if (typeof event.target.className === 'object') {
            return;
        }
        if (this.wrapperRef && !this.wrapperRef.current.contains(event.target)) {
            this.props.onClickClose();
        }
    };

    onChangeBorderInputValue = (e, borderInput) => {
        const { value } = e.target;
        const parsedValue = value ? parseInt(value, 10) : 0;

        this.setState({ [borderInput]: parsedValue || value });
        this.inputToChangeProp[borderInput](parsedValue);
    };

    addToAllButtonBorders = (additionValue, defaultValue) => {
        const sizes = omit(this.state, RADIUS);

        Object.keys(sizes).forEach((sizeName) => {
            const size = (sizes[sizeName] && sizes[sizeName] + additionValue) || defaultValue;

            this.setState({ [sizeName]: size });
            this.inputToChangeProp[sizeName](size);
        });
    };

    onChangeAllButtonBordersUp = () => {
        this.addToAllButtonBorders(1, 1);
    };
    onChangeAllButtonBordersDown = () => {
        this.addToAllButtonBorders(-1, 0);
    };

    render() {
        const { showPopover, onClickClose, anchorEl, minWidth, qaId } = this.props;

        const popoverClassName = `${BASE_CLASS_NAME}__popover`;

        return (
            <SuiPopover open={showPopover} anchorEl={anchorEl}>
                <div ref={this.wrapperRef}>
                    <SuiContainer className={`${BASE_CLASS_NAME} ${popoverClassName}`} style={{ minWidth }}>
                        <SuiContainer className={`${popoverClassName}__header`}>
                            <SuiLabel text={BORDER_PICKER_POPOVER_TITLE} />
                            <SuiButton
                                data-skip-autofocus
                                variation="navigation"
                                size="small"
                                onClick={onClickClose}
                                icon={<CloseIcon />}
                                data-qa={`${qaId}--close`}
                            />
                        </SuiContainer>
                        <SuiContainer className={`${popoverClassName}__body`}>
                            <SuiLabel
                                text={BORDER_PICKER_POPOVER_BODY}
                                className={`${popoverClassName}__body--label`}
                            />
                            <SuiContainer className={`${popoverClassName}__container`}>
                                <SuiContainer className={`${popoverClassName}__row`}>
                                    <div
                                        data-qa={`${qaId}---topBorder`}
                                        className={`${popoverClassName}__input-wrapper`}
                                    >
                                        <SuiTextInput
                                            onFocus={this.setInputFocused}
                                            onBlur={this.unsetInputFocused}
                                            type="number"
                                            min="0"
                                            value={this.state[TOP]}
                                            onChange={(e) => this.onChangeBorderInputValue(e, TOP)}
                                        />
                                    </div>
                                </SuiContainer>
                                <SuiContainer className={`${popoverClassName}__row`}>
                                    <div
                                        data-qa={`${qaId}--leftBorders`}
                                        className={`${popoverClassName}__input-wrapper`}
                                    >
                                        <SuiTextInput
                                            onFocus={this.setInputFocused}
                                            onBlur={this.unsetInputFocused}
                                            type="number"
                                            min="0"
                                            value={this.state[LEFT]}
                                            onChange={(e) => this.onChangeBorderInputValue(e, LEFT)}
                                        />
                                    </div>
                                    <SuiButton
                                        className={`${BASE_CLASS_NAME}--remove`}
                                        data-skip-autofocus
                                        variation="navigation"
                                        size="small"
                                        onClick={this.onChangeAllButtonBordersDown}
                                        icon={<RemoveFill />}
                                        data-qa={`${qaId}--minusBorders`}
                                    />
                                    <SuiButton
                                        className={`${BASE_CLASS_NAME}--add`}
                                        data-skip-autofocus
                                        variation="navigation"
                                        size="small"
                                        onClick={this.onChangeAllButtonBordersUp}
                                        icon={<AddFill />}
                                        data-qa={`${qaId}---plusBorders`}
                                    />
                                    <div
                                        data-qa={`${qaId}--rightBorders`}
                                        className={`${popoverClassName}__input-wrapper`}
                                    >
                                        <SuiTextInput
                                            onFocus={this.setInputFocused}
                                            onBlur={this.unsetInputFocused}
                                            type="number"
                                            min="0"
                                            value={this.state[RIGHT]}
                                            onChange={(e) => this.onChangeBorderInputValue(e, RIGHT)}
                                        />
                                    </div>
                                </SuiContainer>
                                <SuiContainer className={`${popoverClassName}__row`}>
                                    <div
                                        data-qa={`${qaId}--bottomBorder`}
                                        className={`${popoverClassName}__input-wrapper`}
                                    >
                                        <SuiTextInput
                                            onFocus={this.setInputFocused}
                                            onBlur={this.unsetInputFocused}
                                            type="number"
                                            min="0"
                                            value={this.state[BOTTOM]}
                                            onChange={(e) => this.onChangeBorderInputValue(e, BOTTOM)}
                                        />
                                    </div>
                                </SuiContainer>
                            </SuiContainer>
                        </SuiContainer>
                        <SuiContainer className={`${popoverClassName}__footer`}>
                            <SuiLabel
                                text={BORDER_PICKER_POPOVER_FOOTER}
                                className={`${popoverClassName}__footer--label`}
                            />
                            <div data-qa={`${qaId}--radius`} className={`${popoverClassName}__input-wrapper`}>
                                <SuiTextInput
                                    onFocus={this.setInputFocused}
                                    onBlur={this.unsetInputFocused}
                                    type="number"
                                    min="0"
                                    value={this.state[RADIUS]}
                                    onChange={(e) => this.onChangeBorderInputValue(e, RADIUS)}
                                />
                            </div>
                        </SuiContainer>
                    </SuiContainer>
                </div>
            </SuiPopover>
        );
    }
}
